import { CloseOutlined, DeleteOutlined, FileOutlined, InboxOutlined } from '@ant-design/icons'
import { Button, Form, Input, Modal, Progress, Space, Switch, Upload } from 'antd'
import type { RcFile } from 'antd/es/upload'
import { WhatsNewCreatePayload, WhatsNewMediaData } from 'Modules'
import React, { useEffect, useRef, useState } from 'react'
import { toast } from 'react-toastify'

import { AppAdmin } from 'api/app-admin'
import { useCreateWhatsNew, useUpdateWhatsNew, useWhatsNewDetail } from 'hooks/whats-new'
import { formatFileSize } from 'utils/format'

import style from './whats-new-form-modal.module.scss'

interface WhatsNewFormModalProps {
  visible: boolean
  editingId?: string
  onClose: () => void
  onSuccess: () => void
}

const ALLOWED_EXTENSIONS = ['.gif', '.mp4']

const MAX_FILE_SIZE = 10 * 1024 * 1024 // 10 MB

const MSG_MEDIA_REQUIRED = 'Media is required'
const MSG_INVALID_TYPE = 'Only GIF or MP4 files are allowed'
const MSG_FILE_TOO_LARGE = 'File size must not exceed 10 MB'
const MSG_INVALID_URL = 'Invalid URL'

const mediaBadge = (type: string) => (type === 'MP4' ? 'VIDEO' : type)

const isHttpsUrl = (value: string): boolean => {
  try {
    return new URL(value).protocol === 'https:'
  } catch {
    return false
  }
}

export const WhatsNewFormModal: React.FC<WhatsNewFormModalProps> = ({
  visible,
  editingId,
  onClose,
  onSuccess,
}) => {
  const [form] = Form.useForm()
  const [uploadedMedia, setUploadedMedia] = useState<WhatsNewMediaData | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadingFile, setUploadingFile] = useState<{ name: string } | null>(null)
  const [uploadPercent, setUploadPercent] = useState(0)
  const [mediaError, setMediaError] = useState<string | null>(null)
  const [isPublished, setIsPublished] = useState(false)
  const abortRef = useRef<AbortController | null>(null)

  const title = Form.useWatch('title', form)
  const description = Form.useWatch('description', form)

  const { item: editingItem } = useWhatsNewDetail(editingId && visible ? editingId : undefined)
  const createWhatsNew = useCreateWhatsNew()
  const updateWhatsNew = useUpdateWhatsNew()

  useEffect(() => {
    if (editingItem && visible) {
      form.setFieldsValue({
        title: editingItem.title,
        description: editingItem.description,
        buttonLabel: editingItem.buttonLabel,
        linkUrl: editingItem.linkUrl,
      })
      setUploadedMedia(editingItem.media)
      setIsPublished(editingItem.status === 'PUBLISHED')
    } else if (!editingItem && visible && !editingId) {
      form.resetFields()
      setUploadedMedia(null)
      setIsPublished(false)
    }
  }, [editingItem, visible, editingId, form])

  const cancelUpload = () => {
    abortRef.current?.abort()
    abortRef.current = null
    setUploading(false)
    setUploadingFile(null)
    setUploadPercent(0)
  }

  const validateFile = (file: RcFile): string | null => {
    const fileName = file.name.toLowerCase()
    if (!ALLOWED_EXTENSIONS.some((ext) => fileName.endsWith(ext))) return MSG_INVALID_TYPE
    if (file.size > MAX_FILE_SIZE) return MSG_FILE_TOO_LARGE
    return null
  }

  const handleUpload = async (file: RcFile): Promise<void> => {
    const error = validateFile(file)
    if (error) {
      setMediaError(error)
      return
    }

    const controller = new AbortController()
    abortRef.current = controller
    setMediaError(null)
    setUploading(true)
    setUploadingFile({ name: file.name })
    setUploadPercent(0)

    try {
      const formData = new FormData()
      formData.append('file', file)

      const response = await AppAdmin.uploadWhatsNewMedia(formData, {
        signal: controller.signal,
        onUploadProgress: (event) => {
          if (event.total) setUploadPercent(Math.round((event.loaded / event.total) * 100))
        },
      })
      const media = response.data?.data

      if (controller.signal.aborted) return

      if (media?.url) {
        setUploadedMedia(media)
      } else {
        setMediaError('Upload failed')
      }
    } catch (error: any) {
      if (controller.signal.aborted) return
      setMediaError(error?.response?.data?.message || 'Upload failed')
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null
        setUploading(false)
        setUploadingFile(null)
        setUploadPercent(0)
      }
    }
  }

  const handleRemoveMedia = () => {
    setUploadedMedia(null)
    setMediaError(MSG_MEDIA_REQUIRED)
  }

  const handleSubmit = async (values: any) => {
    if (!uploadedMedia) {
      setMediaError(MSG_MEDIA_REQUIRED)
      return
    }

    const payload: WhatsNewCreatePayload = {
      media: uploadedMedia,
      title: values.title.trim(),
      description: values.description.trim(),
      buttonLabel: values.buttonLabel?.trim() || '',
      linkUrl: values.linkUrl?.trim() || '',
      status: isPublished ? 'PUBLISHED' : 'UNPUBLISHED',
    }

    try {
      if (editingId) {
        await updateWhatsNew.mutateAsync({ id: editingId, payload })
        toast.success('Save changes successfully')
      } else {
        await createWhatsNew.mutateAsync(payload)
        toast.success('Create item successfully')
      }
      onSuccess()
      handleClose()
    } catch (error: any) {
      const message = error?.response?.data?.message || 'Failed to save'
      toast.error(message)
    }
  }

  const handleClose = () => {
    cancelUpload()
    form.resetFields()
    setUploadedMedia(null)
    setMediaError(null)
    setIsPublished(false)
    onClose()
  }

  const canSave = !!uploadedMedia && !uploading && !!title?.trim() && !!description?.trim()

  const renderMedia = () => {
    if (uploading && uploadingFile) {
      return (
        <div className={style.uploading}>
          <FileOutlined className={style.fileIcon} />
          <div className={style.uploadingBody}>
            <span className={style.fileName}>{uploadingFile.name}</span>
            <span className={style.fileMeta}>Uploading… {uploadPercent}%</span>
            <Progress percent={uploadPercent} showInfo={false} size="small" />
          </div>
          <Button type="text" size="small" icon={<CloseOutlined />} onClick={cancelUpload} />
        </div>
      )
    }

    if (uploadedMedia) {
      return (
        <div className={style.uploadedMedia}>
          <div className={style.preview}>
            {uploadedMedia.type === 'MP4' ? (
              <video src={uploadedMedia.url} autoPlay muted loop playsInline />
            ) : (
              <img src={uploadedMedia.url} alt={uploadedMedia.fileName} />
            )}
            <span className={style.badge}>{mediaBadge(uploadedMedia.type)}</span>
          </div>
          <div className={style.mediaInfo}>
            <div className={style.mediaText}>
              <span className={style.fileName}>{uploadedMedia.fileName}</span>
              <span className={style.fileMeta}>
                {uploadedMedia.type} · {formatFileSize(uploadedMedia.fileSize)}
              </span>
            </div>
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={handleRemoveMedia}
              title="Remove"
            />
          </div>
        </div>
      )
    }

    return (
      <Upload.Dragger
        accept=".gif,.mp4"
        showUploadList={false}
        beforeUpload={(file) => {
          handleUpload(file)
          return false
        }}
        multiple={false}
        className={`${style.uploader} ${mediaError ? style.uploaderError : ''}`}
      >
        <p className="ant-upload-drag-icon">
          <InboxOutlined />
        </p>
        <p className="ant-upload-text">
          Drag and drop a file here, or <span className={style.browse}>browse</span>
        </p>
        <p className="ant-upload-hint">GIF or MP4, max 10 MB</p>
      </Upload.Dragger>
    )
  }

  return (
    <Modal
      title={editingId ? 'Edit Item' : 'Create Item'}
      open={visible}
      onCancel={handleClose}
      width={600}
      footer={[
        <Button key="cancel" onClick={handleClose}>
          Cancel
        </Button>,
        <Button
          key="submit"
          type="primary"
          disabled={!canSave}
          loading={createWhatsNew.isPending || updateWhatsNew.isPending}
          onClick={() => form.submit()}
        >
          {editingId ? 'Save changes' : 'Save'}
        </Button>,
      ]}
    >
      <Form form={form} onFinish={handleSubmit} layout="vertical" requiredMark>
        {/* Media Upload */}
        <Form.Item
          label="Media"
          required
          validateStatus={mediaError ? 'error' : undefined}
          help={mediaError}
        >
          <div className={style.mediaSection}>{renderMedia()}</div>
        </Form.Item>

        {/* Title */}
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, whitespace: true, message: 'Title is required' }]}
        >
          <Input maxLength={100} placeholder="Enter title" showCount />
        </Form.Item>

        {/* Description */}
        <Form.Item
          name="description"
          label="Description"
          rules={[{ required: true, whitespace: true, message: 'Description is required' }]}
        >
          <Input.TextArea maxLength={300} placeholder="Enter description" showCount rows={4} />
        </Form.Item>

        {/* Button Label */}
        <Form.Item
          name="buttonLabel"
          label="Button label (optional)"
          dependencies={['linkUrl']}
          rules={[
            ({ getFieldValue }) => ({
              validator: (_, value?: string) =>
                !value?.trim() && getFieldValue('linkUrl')?.trim()
                  ? Promise.reject(new Error('Button label is required'))
                  : Promise.resolve(),
            }),
          ]}
        >
          <Input maxLength={20} placeholder="e.g. Explore" showCount />
        </Form.Item>

        {/* Link URL */}
        <Form.Item
          name="linkUrl"
          label="Link URL (optional)"
          dependencies={['buttonLabel']}
          rules={[
            ({ getFieldValue }) => ({
              validator: (_, value?: string) => {
                const url = value?.trim()
                if (!url) {
                  return getFieldValue('buttonLabel')?.trim()
                    ? Promise.reject(new Error('Link URL is required'))
                    : Promise.resolve()
                }
                return isHttpsUrl(url)
                  ? Promise.resolve()
                  : Promise.reject(new Error(MSG_INVALID_URL))
              },
            }),
          ]}
        >
          <Input maxLength={500} placeholder="https://" showCount />
        </Form.Item>

        {/* Status */}
        <Form.Item label="Status">
          <Space>
            <Switch checked={isPublished} onChange={setIsPublished} />
            <span>{isPublished ? 'Published' : 'Unpublished'}</span>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  )
}
